import { useEffect, useRef, useState } from "react";
import { Bell, Bot, ClipboardList, Eye, Headphones, KeyRound, LogOut, Moon, Sun, X } from "lucide-react";
import SupportCenterView from "./SupportCenterView";
import PortalCredentialsView from "./PortalCredentialsView";
import TenantPanelAccessView from "./TenantPanelAccessView";
import TeknoparkAIAdminView from "./TeknoparkAIAdminView";
import DemoRequestKanbanView from "./DemoRequestKanbanView";
import {
  markNotificationRead,
  subscribeSupportNotifications
} from "../services/notificationService";

const MANAGEMENT_TAB_IDS = ["demo-requests", "support", "portals", "tenants", "ai"];
const MANAGEMENT_TAB_STORAGE_KEY = "argevio.management.activeTab";

const getInitialManagementTab = () => {
  if (typeof window === "undefined") return "support";
  const hashTab = window.location.hash.replace(/^#/, "");
  if (MANAGEMENT_TAB_IDS.includes(hashTab)) return hashTab;
  const savedTab = window.localStorage.getItem(MANAGEMENT_TAB_STORAGE_KEY);
  return MANAGEMENT_TAB_IDS.includes(savedTab) ? savedTab : "support";
};

function SupportManagementDashboard({ user, userData, users, onLogout, notifyTeam, actorName }) {
  const [active, setActive] = useState(getInitialManagementTab);
  const [dark, setDark] = useState(false);
  const [supportNotifications, setSupportNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [toast, setToast] = useState(null);
  const notificationIdsRef = useRef(new Set());
  const notificationsReadyRef = useRef(false);
  const tabs = [
    { id: "demo-requests", label: "Demo Talepleri", icon: ClipboardList },
    { id: "support", label: "Destek Merkezi", icon: Headphones },
    { id: "portals", label: "Portal Erişimleri", icon: KeyRound },
    { id: "tenants", label: "Müşteri Panelleri", icon: Eye },
    { id: "ai", label: "Teknopark AI", icon: Bot }
  ];

  useEffect(() => {
    window.localStorage.setItem(MANAGEMENT_TAB_STORAGE_KEY, active);
    if (window.location.hash !== `#${active}`) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${active}`);
    }
  }, [active]);

  useEffect(() => {
    const handleHashChange = () => {
      const hashTab = window.location.hash.replace(/^#/, "");
      if (MANAGEMENT_TAB_IDS.includes(hashTab)) setActive(hashTab);
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => subscribeSupportNotifications((items) => {
    setSupportNotifications(items);
    const nextIds = new Set(items.map((item) => item.id));
    if (!notificationsReadyRef.current) {
      notificationIdsRef.current = nextIds;
      notificationsReadyRef.current = true;
      return;
    }
    const incoming = items.find((item) =>
      !item.isRead && !notificationIdsRef.current.has(item.id)
    );
    notificationIdsRef.current = nextIds;
    if (!incoming) return;

    setToast(incoming);
    window.setTimeout(() => {
      setToast((current) => current?.id === incoming.id ? null : current);
    }, 8000);
    if ("Notification" in window && window.Notification.permission === "granted") {
      try {
        new window.Notification(incoming.title, { body: incoming.body });
      } catch {
        // Uygulama içi bildirim görünmeye devam eder.
      }
    }
  }, (error) => console.error("Destek bildirimleri alınamadı:", error)), []);

  const openSupportNotification = async (notification) => {
    const isDemoRequest = notification.type === "demo-request" || notification.link === "demo-requests";
    setActive(isDemoRequest ? "demo-requests" : "support");
    setSelectedTaskId(isDemoRequest ? (notification.taskId || "") : "");
    setSelectedTicketId(isDemoRequest ? "" : (notification.ticketId || ""));
    setNotificationsOpen(false);
    setToast(null);
    if (!notification.isRead) {
      try {
        await markNotificationRead(notification.id);
      } catch (error) {
        console.error("Destek bildirimi okundu işaretlenemedi:", error);
      }
    }
  };

  const unreadSupportCount = supportNotifications.filter((item) => !item.isRead).length;
  const unreadDemoCount = supportNotifications.filter((item) =>
    !item.isRead && (item.type === "demo-request" || item.link === "demo-requests")
  ).length;
  const unreadTicketCount = unreadSupportCount - unreadDemoCount;

  return <div style={{ minHeight: "100vh", display: "grid", gridTemplateColumns: "230px 1fr", background: dark ? "#0f172a" : "#f5f7fb" }}>
    <aside style={{ background: "linear-gradient(180deg,#124254,#0f3442)", color: "#fff", padding: 18, display: "flex", flexDirection: "column" }}>
      <div style={{ marginBottom: 26 }}>
        <img
          src="/argevio-flow-logo-dark.svg"
          alt="Ar-Ge vio — Ar-Ge Operasyon Platformu"
          style={{ display: "block", width: 184, maxWidth: "100%", height: "auto" }}
        />
        <div style={{ opacity: .72, fontSize: 11, fontWeight: 750, marginTop: -5, paddingLeft: 3 }}>
          Destek Operasyon Merkezi
        </div>
      </div>
      <nav style={{ display: "grid", gap: 7 }}>{tabs.map(({ id, label, icon: Icon }) => <button key={id} onClick={() => setActive(id)} style={{ border: 0, borderRadius: 11, padding: "12px 13px", background: active === id ? "rgba(255,255,255,.16)" : "transparent", color: "#fff", textAlign: "left", fontWeight: 800, display: "flex", alignItems: "center", gap: 9, cursor: "pointer" }}><Icon size={18}/>{label}{id === "demo-requests" && unreadDemoCount > 0 && <span style={navBadge}>{unreadDemoCount}</span>}{id === "support" && unreadTicketCount > 0 && <span style={navBadge}>{unreadTicketCount}</span>}</button>)}</nav>
      <button onClick={() => setNotificationsOpen((value) => !value)} style={{ ...bottomButton, marginTop: 14, position: "relative" }}>
        <Bell size={17}/> Bildirimler
        {unreadSupportCount > 0 && <span style={navBadge}>{unreadSupportCount}</span>}
      </button>
      <div style={{ marginTop: "auto", display: "grid", gap: 7 }}>
        <div style={{ padding: 11, borderRadius: 11, background: "rgba(255,255,255,.08)", fontSize: 12, overflow: "hidden" }}>
          <strong style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>{userData?.name || user?.email}</strong>
          <div style={{ opacity: .7, marginTop: 3 }}>Destek yöneticisi</div>
          <button onClick={onLogout} style={profileLogoutButton}><LogOut size={15}/> Çıkış Yap</button>
        </div>
        <button onClick={() => setDark((value) => !value)} style={bottomButton}>{dark ? <Sun size={17}/> : <Moon size={17}/>} {dark ? "Açık tema" : "Koyu tema"}</button>
      </div>
    </aside>
    <main style={{ minWidth: 0, padding: "34px 40px", color: dark ? "#e5edf6" : "#124254" }}>
      {active === "demo-requests" && <DemoRequestKanbanView user={user} users={users} themeMode={dark ? "dark" : "light"} initialTaskId={selectedTaskId} onInitialTaskHandled={() => setSelectedTaskId("")}/>} 
      {active === "support" && <SupportCenterView user={user} userData={userData} users={users} themeMode={dark ? "dark" : "light"} initialTicketId={selectedTicketId} onInitialTicketHandled={() => setSelectedTicketId("")}/>} 
      {active === "portals" && <PortalCredentialsView credentialScope={{ kind: "management" }} notifyTeam={notifyTeam} actorName={actorName}/>} 
      {active === "tenants" && <TenantPanelAccessView themeMode={dark ? "dark" : "light"}/>} 
      {active === "ai" && <TeknoparkAIAdminView user={user} themeMode={dark ? "dark" : "light"}/>}
    </main>
    {notificationsOpen && <div style={{ position: "fixed", left: 238, top: 90, width: 360, maxHeight: "70vh", overflowY: "auto", zIndex: 1200, padding: 12, borderRadius: 16, background: dark ? "#172235" : "#fff", border: `1px solid ${dark ? "#334155" : "#dbe2ea"}`, boxShadow: "0 20px 55px rgba(15,23,42,.22)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "3px 4px 10px" }}>
        <strong>Destek bildirimleri</strong>
        <button onClick={() => setNotificationsOpen(false)} aria-label="Bildirimleri kapat" style={iconButton}><X size={16}/></button>
      </div>
      {!supportNotifications.length && <div style={{ padding: 20, textAlign: "center", opacity: .7 }}>Henüz destek bildirimi yok.</div>}
      {supportNotifications.map((notification) => <button key={notification.id} onClick={() => openSupportNotification(notification)} style={{ width: "100%", textAlign: "left", border: `1px solid ${notification.isRead ? (dark ? "#334155" : "#e2e8f0") : "#5eead4"}`, borderRadius: 12, padding: 12, marginBottom: 8, background: notification.isRead ? "transparent" : (dark ? "#123f38" : "#ecfdf5"), color: dark ? "#e5edf6" : "#124254", cursor: "pointer" }}>
        <strong style={{ display: "block", fontSize: 13 }}>{notification.title}</strong>
        <span style={{ display: "block", marginTop: 5, fontSize: 12, lineHeight: 1.45, opacity: .8 }}>{notification.body}</span>
      </button>)}
    </div>}
    {toast && <button onClick={() => openSupportNotification(toast)} style={{ position: "fixed", right: 24, top: 24, zIndex: 1400, width: 360, textAlign: "left", border: "1px solid #5eead4", borderRadius: 16, padding: 15, background: dark ? "#123f38" : "#ecfdf5", color: dark ? "#d1fae5" : "#115e59", boxShadow: "0 20px 55px rgba(15,23,42,.25)", cursor: "pointer" }}>
      <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 950 }}><Bell size={18}/>{toast.title}</span>
      <span style={{ display: "block", marginTop: 7, lineHeight: 1.45 }}>{toast.body}</span>
      <small style={{ display: "block", marginTop: 9, fontWeight: 850 }}>{toast.type === "demo-request" || toast.link === "demo-requests" ? "Görevi aç →" : "Görüşmeyi aç →"}</small>
    </button>}
  </div>;
}

const bottomButton = { border: "1px solid rgba(255,255,255,.12)", borderRadius: 10, padding: "10px 11px", background: "rgba(255,255,255,.06)", color: "#fff", display: "flex", alignItems: "center", gap: 8, cursor: "pointer" };
const profileLogoutButton = { width: "100%", marginTop: 10, border: "1px solid rgba(254,202,202,.28)", borderRadius: 9, padding: "8px 9px", background: "rgba(239,68,68,.16)", color: "#fecaca", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, fontWeight: 850, cursor: "pointer" };
const navBadge = { marginLeft: "auto", minWidth: 20, height: 20, padding: "0 6px", borderRadius: 999, display: "inline-grid", placeItems: "center", background: "#ef4444", color: "#fff", fontSize: 11, fontWeight: 950 };
const iconButton = { width: 30, height: 30, display: "grid", placeItems: "center", border: "1px solid #cbd5e1", borderRadius: 9, background: "transparent", cursor: "pointer" };
export default SupportManagementDashboard;
